import { createPortal } from "react-dom";
import { useLayoutEffect, useState } from "react";

const MENU_OFFSET = 6;
const VIEWPORT_PAD = 8;
const MENU_WIDTH = 260;
const FALLBACK_HEIGHT = 360;

/**
 * Renders action menus on document.body so backdrop-filter samples the full
 * page (same behaviour as header search / notification panes).
 * Opens below the anchor, right-aligned to the ⋯ (flips above if needed).
 */
export default function PortalInteractionMenu({
  show,
  anchorRef,
  menuRef,
  className = "",
  preferAbove = false,
  compact = false,
  minWidth,
  maxWidth,
  children,
}) {
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!show || !anchorRef?.current) {
      setCoords(null);
      return undefined;
    }

    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const menuEl = menuRef?.current;
      const fallbackWidth = minWidth ?? MENU_WIDTH;
      const widthCap = Math.min(maxWidth ?? 280, window.innerWidth - VIEWPORT_PAD * 2);
      const measured = menuEl?.offsetWidth || fallbackWidth;
      const menuWidth = compact
        ? Math.min(measured || 120, maxWidth ?? 160)
        : Math.min(Math.max(measured, fallbackWidth), widthCap);
      const menuHeight = menuEl?.offsetHeight || (preferAbove ? 120 : FALLBACK_HEIGHT);

      let top = preferAbove
        ? rect.top - menuHeight - MENU_OFFSET
        : rect.bottom + MENU_OFFSET;

      if (!preferAbove && top + menuHeight > window.innerHeight - VIEWPORT_PAD) {
        top = Math.max(
          VIEWPORT_PAD,
          rect.top - menuHeight - MENU_OFFSET
        );
      }

      if (preferAbove && top < VIEWPORT_PAD) {
        top = Math.min(
          window.innerHeight - menuHeight - VIEWPORT_PAD,
          rect.bottom + MENU_OFFSET
        );
      }

      const right = Math.max(VIEWPORT_PAD, window.innerWidth - rect.right);
      let left = rect.right - menuWidth;
      left = Math.min(
        Math.max(VIEWPORT_PAD, left),
        window.innerWidth - menuWidth - VIEWPORT_PAD
      );

      setCoords({ top, left, right, width: menuWidth, compact });
    };

    updatePosition();
    const raf = requestAnimationFrame(updatePosition);

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [show, anchorRef, menuRef, preferAbove, compact, minWidth, maxWidth]);

  if (!show || !coords) {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      className={`dropdown-menu show InteractionModal wlj-portal-interaction-menu ${className}`.trim()}
      style={
        coords.compact
          ? {
              position: "fixed",
              top: coords.top,
              left: "auto",
              right: coords.right,
              bottom: "auto",
              width: "max-content",
              minWidth: 0,
              maxWidth: maxWidth ?? 160,
              transform: "none",
              display: "block",
            }
          : {
              position: "fixed",
              top: coords.top,
              left: coords.left,
              right: "auto",
              bottom: "auto",
              width: coords.width,
              maxWidth: "min(280px, calc(100vw - 24px))",
              transform: "none",
              display: "block",
            }
      }
    >
      {children}
    </div>,
    document.body
  );
}
